import React from "react";
import { Link } from "react-router-dom";
import { Download } from "lucide-react";
import Layout from "../components/Layout";
import SEO from "../components/SEO";
import { IMPORTANT_FACTS as DISCLOSURES } from "../data/importantFacts";

const Disclosures = () => {
  return (
    <Layout>
      <SEO
        title="Operating Outlook | Steck Aviation Management"
        description="How Steck Aviation Management operates: how we are paid, what we do, what we will not do, and the regulatory frame our work sits within."
      />

      {/* ── Gunmetal + teal glass header ─────────────────────────────────────
          Deep metallic gradient with a teal ambient glow and fine grid —
          the same technical glass aesthetic as the research desk / flight chart.
          Hidden in print so only the white-paper articles run off the page.    */}
      <div
        className="print:hidden"
        style={{
          position: "relative",
          overflow: "hidden",
          background: [
            "radial-gradient(ellipse 72% 55% at 68% 36%, rgba(0,185,185,0.13) 0%, transparent 62%)",
            "radial-gradient(ellipse 38% 60% at 92% 80%, rgba(103,232,249,0.06) 0%, transparent 55%)",
            "linear-gradient(180deg, rgba(255,255,255,0.035) 0%, transparent 22%)",
            "linear-gradient(148deg, #1C2B38 0%, #1E3347 32%, #172F3E 62%, #0E2030 100%)",
          ].join(", "),
        }}
      >
        {/* Fine technical grid */}
        <div aria-hidden style={{
          position: "absolute", inset: 0, pointerEvents: "none", zIndex: 1,
          backgroundImage: [
            "linear-gradient(rgba(103,232,249,0.030) 1px, transparent 1px)",
            "linear-gradient(90deg, rgba(103,232,249,0.030) 1px, transparent 1px)",
          ].join(", "),
          backgroundSize: "76px 76px",
        }} />

        <div style={{
          position: "relative", zIndex: 2,
          maxWidth: 1100, margin: "0 auto",
          padding: "clamp(44px,5.5vh,72px) clamp(24px,6vw,80px) clamp(52px,7vh,88px)",
        }}>
          {/* Nav controls */}
          <div className="flex items-center justify-between print:hidden" style={{ marginBottom: "clamp(36px,5vh,60px)" }}>
            <Link
              to="/"
              data-testid="disclosures-back"
              style={{
                fontSize: 13, fontWeight: 500,
                color: "rgba(156,177,195,0.80)",
                display: "inline-flex", alignItems: "center", gap: 8,
                textDecoration: "none", transition: "color 220ms",
              }}
              onMouseEnter={e => (e.currentTarget.style.color = "#F4F1EB")}
              onMouseLeave={e => (e.currentTarget.style.color = "rgba(156,177,195,0.80)")}
            >
              ← Back
            </Link>
            <button
              onClick={() => window.print()}
              data-testid="disclosures-pdf"
              className="btn-ghost"
              style={{ padding: "0.65rem 1.1rem", fontSize: "0.76rem" }}
            >
              <Download size={13} /> Save as PDF
            </button>
          </div>

          {/* Eyebrow */}
          <div
            className="font-mono-label"
            style={{ color: "#67E8F9", marginBottom: "1.25rem" }}
          >
            Operating outlook
          </div>

          {/* Title */}
          <h1
            className="font-display-lg"
            style={{ color: "#F4F1EB", fontSize: "clamp(36px,4.8vw,64px)", lineHeight: 1.04, margin: 0 }}
          >
            The frame we operate within.
          </h1>

          {/* Intro */}
          <p style={{
            color: "rgba(244,241,235,0.58)",
            marginTop: "1.5rem",
            maxWidth: 560,
            lineHeight: 1.78,
            fontSize: 15.5,
          }}>
            Steck Aviation Management is a private management-services firm working
            exclusively for aircraft owners. The brief below sets out how we are
            paid, what we do, what we will not do, and the regulatory frame our
            work sits within. It is deliberately short.
          </p>
        </div>
      </div>

      {/* ── Your funds. Protected. ───────────────────────────────────────────
          Relocated here from the aircraft cost sheet as a more fitting home.
          Placement and format are provisional—kept close to its original dark
          treatment (gold separation line, serif headline, three pillars) so it
          reads correctly while the final layout is decided.                   */}
      <section className="print:hidden" style={{ background: "#000", position: "relative" }}>
        {/* Gold separation line—kept from its original placement */}
        <div aria-hidden style={{ height: 2, background: "linear-gradient(to right, transparent 0%, rgba(200,168,112,0.60) 30%, rgba(200,168,112,0.60) 70%, transparent 100%)" }} />
        <div style={{ maxWidth: 1500, margin: "0 auto", padding: "0 clamp(24px,6vw,80px)" }}>
          <div style={{ padding: "clamp(64px,8vh,108px) 0 clamp(40px,5vh,64px)" }}>
            <h2 style={{
              fontFamily: "Fraunces, serif", fontSize: "clamp(44px,5.6vw,92px)", fontWeight: 900,
              color: "#fff", margin: 0, lineHeight: 0.94, letterSpacing: "-0.025em",
            }}>
              Your funds.<br />Protected.
            </h2>
          </div>

          {/* Three pillars */}
          <div className="grid grid-cols-1 md:grid-cols-3" style={{ gap: "clamp(28px,3vw,56px)" }}>
            {[
              {
                title: "Pre-Funded",
                body: "Each owner's allocated portion of anticipated fixed operating costs is funded in advance, helping maintain predictable reserves for recurring aircraft obligations.",
              },
              {
                title: "Itemized",
                body: "Every withdrawal has a line-itemed invoice attached. Hangar, fuel, maintenance, and every other expense the aircraft incurs. Nothing pooled. Nothing opaque.",
              },
              {
                title: "Yours",
                body: "The account belongs to the ownership group, not Steck Aviation Management. Current balances and full transaction history are available on request.",
              },
            ].map(({ title, body }) => (
              <div key={title}>
                <div style={{ width: "clamp(20px,2vw,28px)", height: 2, borderRadius: 1, background: "#7DD8EA", marginBottom: 20 }} />
                <div style={{ fontFamily: "Fraunces, serif", fontSize: "clamp(23px,2.3vw,32px)", fontWeight: 700, color: "#fff", marginBottom: 16, lineHeight: 1.08 }}>{title}</div>
                <p style={{ fontSize: "clamp(15px,1.2vw,17px)", color: "rgba(255,255,255,0.78)", lineHeight: 1.86, margin: 0 }}>{body}</p>
              </div>
            ))}
          </div>
          <div style={{ height: "clamp(56px,8vh,96px)" }} />
        </div>
      </section>

      {/* Steck cyan accent rule—bridges header to paper zone */}
      <div aria-hidden className="print:hidden" style={{ height: 1, background: "rgba(103,232,249,0.28)" }} />

      {/* ── Paper content zone ───────────────────────────────────────────────
          White editorial ground—same data-theme as the research pages.
          disclosures-print class keeps the print-mode styles working.        */}
      <div data-theme="paper" className="disclosures-print">
        <div style={{
          maxWidth: 1100, margin: "0 auto",
          padding: "clamp(48px,6vh,80px) clamp(24px,6vw,80px) clamp(64px,8vh,100px)",
        }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 1, background: "#D4CFC5" }}>
            {DISCLOSURES.map((d) => {
              const Icon = d.icon;
              return (
                <article
                  key={d.id}
                  data-testid={`disclosure-${d.id}`}
                  className="grid grid-cols-1 md:grid-cols-12 gap-8"
                  style={{ background: "#F8F7F4", padding: "clamp(32px,4vw,48px) clamp(24px,4vw,48px)" }}
                >
                  <div className="md:col-span-3">
                    <div
                      className="font-mono-label"
                      style={{ color: "#002868", marginBottom: 16 }}
                    >
                      {d.eyebrow}
                    </div>
                    <Icon size={18} style={{ color: "#8A8A8A" }} />
                  </div>
                  <div className="md:col-span-9">
                    <h2
                      className="font-display"
                      style={{ color: "#0C0C0C", fontSize: "clamp(20px,2vw,26px)", lineHeight: 1.2, marginBottom: 20 }}
                    >
                      {d.title}
                    </h2>
                    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                      {d.body.map((p, i) => (
                        <p key={i} style={{ color: "#2C2C2C", lineHeight: 1.85, fontSize: 15.5, margin: 0 }}>
                          {p}
                        </p>
                      ))}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </div>

    </Layout>
  );
};

export default Disclosures;
